/**
 * Unified input: keyboard / mouse / touch / gamepad → per-tick input words.
 * The DOM side only sets "is the primary action currently down"; the game loop
 * samples it once per sim tick, which keeps the sim deterministic and lets
 * replays reproduce behavior exactly.
 */
import { INPUT_PRIMARY, INPUT_LEFT, INPUT_RIGHT } from '../engine/sim';

export interface InputBindings {
  keys: string[]; // KeyboardEvent.code values for the primary action
  leftKeys: string[];
  rightKeys: string[];
  gamepadButtons: number[]; // standard-mapping button indices
}

export const DEFAULT_BINDINGS: InputBindings = {
  keys: ['Space', 'ArrowUp', 'KeyW'],
  leftKeys: ['ArrowLeft', 'KeyA'],
  rightKeys: ['ArrowRight', 'KeyD'],
  gamepadButtons: [0, 1, 2, 3], // face buttons
};

/** True when the key event belongs to a focused text field / editable UI —
 * gameplay bindings must never swallow typing (e.g. "a"/"w"/"d" in the
 * editor inspector). */
export function isEditableTarget(e: Event): boolean {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export class InputSystem {
  private keyDown = new Set<string>();
  private pointerDown = false;
  private bindings: InputBindings = DEFAULT_BINDINGS;
  /** Callbacks for shell-level (non-sim) actions. */
  onPause: (() => void) | null = null;
  onRestart: (() => void) | null = null;
  onPlaceCheckpoint: (() => void) | null = null;
  onRemoveCheckpoint: (() => void) | null = null;

  attach(target: HTMLElement): void {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      // Typing in a text field always wins over gameplay bindings
      // (Escape still pauses — it never inserts a character).
      if (isEditableTarget(e) && e.code !== 'Escape') return;
      if (e.code === 'Escape' && this.onPause) {
        this.onPause();
        return;
      }
      if (e.code === 'KeyR' && this.onRestart) {
        this.onRestart();
        return;
      }
      if (e.code === 'KeyZ' && this.onPlaceCheckpoint) {
        this.onPlaceCheckpoint();
        return;
      }
      if (e.code === 'KeyX' && this.onRemoveCheckpoint) {
        this.onRemoveCheckpoint();
        return;
      }
      if (
        this.bindings.keys.includes(e.code) ||
        this.bindings.leftKeys.includes(e.code) ||
        this.bindings.rightKeys.includes(e.code)
      ) {
        this.keyDown.add(e.code);
        e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => {
      this.keyDown.delete(e.code);
    });
    window.addEventListener('blur', () => {
      this.keyDown.clear();
      this.pointerDown = false;
    });

    const down = (e: Event): void => {
      // Ignore presses on UI overlays; only the game surface drives the sim.
      if (e.target !== target) return;
      this.pointerDown = true;
      e.preventDefault();
    };
    const up = (): void => {
      this.pointerDown = false;
    };
    target.addEventListener('pointerdown', down);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  setBindings(b: InputBindings): void {
    this.bindings = b;
  }

  /** Edge state for gamepad shell buttons (Start=9 pause, Select=8 restart). */
  private padStartWas = false;
  private padSelectWas = false;

  /** Poll non-sim gamepad buttons; call once per rendered frame. */
  pollShellButtons(): void {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return;
    let start = false;
    let select = false;
    for (const pad of navigator.getGamepads()) {
      if (!pad) continue;
      if (pad.buttons[9]?.pressed) start = true;
      if (pad.buttons[8]?.pressed) select = true;
    }
    if (start && !this.padStartWas) this.onPause?.();
    if (select && !this.padSelectWas) this.onRestart?.();
    this.padStartWas = start;
    this.padSelectWas = select;
  }

  private gamepadHeld(): boolean {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return false;
    for (const pad of navigator.getGamepads()) {
      if (!pad) continue;
      for (const idx of this.bindings.gamepadButtons) {
        if (pad.buttons[idx]?.pressed) return true;
      }
    }
    return false;
  }

  /** Touch left/right buttons (platformer): set by the UI overlay. */
  touchLeft = false;
  touchRight = false;

  private anyKey(codes: string[]): boolean {
    for (const code of codes) if (this.keyDown.has(code)) return true;
    return false;
  }

  private gamepadAxis(): number {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return 0;
    for (const pad of navigator.getGamepads()) {
      if (!pad) continue;
      const x = pad.axes[0] ?? 0;
      if (x < -0.35 || pad.buttons[14]?.pressed) return -1;
      if (x > 0.35 || pad.buttons[15]?.pressed) return 1;
    }
    return 0;
  }

  /** Sample the current input word; called exactly once per sim tick. */
  sampleWord(): number {
    let word = 0;
    if (this.anyKey(this.bindings.keys) || this.pointerDown || this.gamepadHeld()) {
      word |= INPUT_PRIMARY;
    }
    const axis = this.gamepadAxis();
    if (this.anyKey(this.bindings.leftKeys) || this.touchLeft || axis < 0) word |= INPUT_LEFT;
    if (this.anyKey(this.bindings.rightKeys) || this.touchRight || axis > 0) word |= INPUT_RIGHT;
    return word;
  }
}
